import { useState, useEffect, useCallback } from 'react';
import {
  PieChart,
  BarChart2,
  Award,
  Users,
  Layers,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { segmentsApi } from '@/api/segments';
import type {
  AccountPerformanceResponse,
  CategoryAnalysisResponse,
  ScenarioAnalysisResponse,
  BenchmarkResponse,
} from '@/types/data';
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

type TabType = 'accounts' | 'categories' | 'scenarios' | 'benchmarks';

export default function SegmentAnalysis() {
  const { accessibleCompanies } = useAuthStore();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('accounts');
  const [periodDays, setPeriodDays] = useState(30);
  const [isLoading, setIsLoading] = useState(false);

  // データ
  const [accountData, setAccountData] = useState<AccountPerformanceResponse | null>(null);
  const [categoryData, setCategoryData] = useState<CategoryAnalysisResponse | null>(null);
  const [scenarioData, setScenarioData] = useState<ScenarioAnalysisResponse | null>(null);
  const [benchmarkData, setBenchmarkData] = useState<BenchmarkResponse | null>(null);

  // 初期化
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  const tabs = [
    { id: 'accounts' as const, label: 'アカウント別', icon: Users },
    { id: 'categories' as const, label: 'カテゴリ別', icon: Layers },
    { id: 'scenarios' as const, label: 'シナリオ別', icon: BarChart2 },
    { id: 'benchmarks' as const, label: 'ベンチマーク', icon: Award },
  ];

  // アカウント別パフォーマンス取得
  const fetchAccountData = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    try {
      const data = await segmentsApi.getAccountPerformance({
        company_id: selectedCompanyId,
        period_days: periodDays,
      });
      setAccountData(data);
    } catch (err) {
      console.error('アカウントデータ取得エラー:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId, periodDays]);

  // カテゴリ別分析取得
  const fetchCategoryData = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    try {
      const data = await segmentsApi.getCategoryAnalysis({
        company_id: selectedCompanyId,
        period_days: periodDays,
      });
      setCategoryData(data);
    } catch (err) {
      console.error('カテゴリデータ取得エラー:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId, periodDays]);

  // シナリオ別分析取得
  const fetchScenarioData = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    try {
      const data = await segmentsApi.getScenarioAnalysis({
        company_id: selectedCompanyId,
        period_days: periodDays,
      });
      setScenarioData(data);
    } catch (err) {
      console.error('シナリオデータ取得エラー:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId, periodDays]);

  // ベンチマーク比較取得
  const fetchBenchmarkData = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    try {
      const data = await segmentsApi.getBenchmarkComparison({
        company_id: selectedCompanyId,
        period_days: periodDays,
      });
      setBenchmarkData(data);
    } catch (err) {
      console.error('ベンチマークデータ取得エラー:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId, periodDays]);

  // タブ切り替え時にデータ取得
  useEffect(() => {
    if (!selectedCompanyId) return;
    switch (activeTab) {
      case 'accounts':
        fetchAccountData();
        break;
      case 'categories':
        fetchCategoryData();
        break;
      case 'scenarios':
        fetchScenarioData();
        break;
      case 'benchmarks':
        fetchBenchmarkData();
        break;
    }
  }, [activeTab, selectedCompanyId, periodDays, fetchAccountData, fetchCategoryData, fetchScenarioData, fetchBenchmarkData]);

  const getRankColor = (rank: string) => {
    switch (rank) {
      case 'A':
        return 'text-green-600 bg-green-100';
      case 'B':
        return 'text-blue-600 bg-blue-100';
      case 'C':
        return 'text-yellow-600 bg-yellow-100';
      case 'D':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'above':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'below':
        return <TrendingDown className="w-4 h-4 text-red-600" />;
      default:
        return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">セグメント分析</h1>
          <p className="text-gray-500 mt-1">
            アカウント、カテゴリ、シナリオ別のパフォーマンスを分析
          </p>
        </div>

        <div className="flex items-center gap-4">
          {accessibleCompanies.length > 1 && (
            <label className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">法人:</span>
              <select
                value={selectedCompanyId || ''}
                onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-primary focus:border-primary"
              >
                {accessibleCompanies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.display_name || company.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">期間:</span>
            <select
              value={periodDays}
              onChange={(e) => setPeriodDays(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-primary focus:border-primary"
            >
              <option value={7}>過去7日間</option>
              <option value={14}>過去14日間</option>
              <option value={30}>過去30日間</option>
              <option value={60}>過去60日間</option>
              <option value={90}>過去90日間</option>
            </select>
          </label>
        </div>
      </div>

      {/* 法人未選択時のメッセージ */}
      {!selectedCompanyId && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center text-gray-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>法人を選択してください</p>
          </div>
        </div>
      )}

      {/* タブ */}
      {selectedCompanyId && (
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-4 px-4" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* アカウント別 */}
              {activeTab === 'accounts' && accountData && (
                <div className="space-y-6">
                  {/* サマリーカード */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm text-blue-600 font-medium">総アカウント数</p>
                      <p className="text-2xl font-bold text-blue-700">
                        {accountData.total_accounts}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm text-green-600 font-medium">総日次値</p>
                      <p className="text-2xl font-bold text-green-700">
                        {(accountData.summary?.total_daily_value ?? 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <p className="text-sm text-purple-600 font-medium">アカウント平均</p>
                      <p className="text-2xl font-bold text-purple-700">
                        {Math.round(accountData.summary?.avg_per_account ?? 0).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* チャートとテーブル */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* 棒グラフ */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">
                        アカウント別日次値
                      </h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={(accountData.accounts ?? []).slice(0, 10)}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis
                            type="category"
                            dataKey="account_name"
                            width={70}
                            tick={{ fontSize: 11 }}
                          />
                          <Tooltip
                            formatter={(value: number) => [value.toLocaleString(), '日次値合計']}
                          />
                          <Bar dataKey="total_daily_value" fill="#3B82F6" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* ランキングテーブル */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">
                        パフォーマンスランキング
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-gray-500 border-b">
                              <th className="pb-2 font-medium">順位</th>
                              <th className="pb-2 font-medium">アカウント</th>
                              <th className="pb-2 font-medium text-right">日次値</th>
                              <th className="pb-2 font-medium text-right">日平均</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(accountData.accounts ?? []).slice(0, 10).map((account) => (
                              <tr key={account.account_id} className="border-b border-gray-100">
                                <td className="py-2">
                                  <span
                                    className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                                      account.rank <= 3
                                        ? 'bg-primary text-white'
                                        : 'bg-gray-200 text-gray-600'
                                    }`}
                                  >
                                    {account.rank}
                                  </span>
                                </td>
                                <td className="py-2 font-medium text-gray-900">
                                  {account.account_name}
                                </td>
                                <td className="py-2 text-right text-gray-700">
                                  {account.total_daily_value.toLocaleString()}
                                </td>
                                <td className="py-2 text-right text-gray-500">
                                  {account.avg_daily_value.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* カテゴリ別 */}
              {activeTab === 'categories' && categoryData && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* 円グラフ */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">
                        カテゴリ構成比
                      </h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <RechartsPie>
                          <Pie
                            data={categoryData.categories ?? []}
                            dataKey="total_daily_value"
                            nameKey="category"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={({ category, percentage }) =>
                              `${category}: ${percentage?.toFixed(1) ?? 0}%`
                            }
                          >
                            {(categoryData.categories ?? []).map((_entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value: number) => [value.toLocaleString(), '日次値']}
                          />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>

                    {/* テーブル */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">
                        カテゴリ別詳細
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-gray-500 border-b">
                              <th className="pb-2 font-medium">カテゴリ</th>
                              <th className="pb-2 font-medium text-right">ラベル数</th>
                              <th className="pb-2 font-medium text-right">日次値</th>
                              <th className="pb-2 font-medium text-right">構成比</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(categoryData.categories ?? []).map((cat, index) => (
                              <tr key={cat.category} className="border-b border-gray-100">
                                <td className="py-2">
                                  <span className="flex items-center gap-2">
                                    <span
                                      className="w-3 h-3 rounded-full"
                                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                    />
                                    <span className="font-medium text-gray-900">
                                      {cat.category}
                                    </span>
                                  </span>
                                </td>
                                <td className="py-2 text-right text-gray-700">
                                  {cat.label_count}
                                </td>
                                <td className="py-2 text-right text-gray-700">
                                  {(cat.total_daily_value ?? 0).toLocaleString()}
                                </td>
                                <td className="py-2 text-right text-gray-500">
                                  {(cat.percentage ?? 0).toFixed(1)}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* シナリオ別 */}
              {activeTab === 'scenarios' && scenarioData && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* 棒グラフ */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">
                        シナリオ別日次値
                      </h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={(scenarioData.scenarios ?? []).slice(0, 10)}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="scenario"
                            tick={{ fontSize: 11 }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis />
                          <Tooltip
                            formatter={(value: number) => [value.toLocaleString(), '日次値合計']}
                          />
                          <Bar dataKey="total_daily_value" fill="#10B981" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* テーブル */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">
                        シナリオ別詳細
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-gray-500 border-b">
                              <th className="pb-2 font-medium">シナリオ</th>
                              <th className="pb-2 font-medium text-right">ラベル数</th>
                              <th className="pb-2 font-medium text-right">日次値</th>
                              <th className="pb-2 font-medium text-right">構成比</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(scenarioData.scenarios ?? []).map((scenario) => (
                              <tr key={scenario.scenario} className="border-b border-gray-100">
                                <td className="py-2 font-medium text-gray-900">
                                  {scenario.scenario}
                                </td>
                                <td className="py-2 text-right text-gray-700">
                                  {scenario.label_count}
                                </td>
                                <td className="py-2 text-right text-gray-700">
                                  {(scenario.total_daily_value ?? 0).toLocaleString()}
                                </td>
                                <td className="py-2 text-right text-gray-500">
                                  {(scenario.percentage ?? 0).toFixed(1)}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ベンチマーク */}
              {activeTab === 'benchmarks' && benchmarkData && (
                <div className="space-y-6">
                  {/* 総合スコア */}
                  <div className="flex items-center justify-center">
                    <div className="text-center bg-gray-50 rounded-lg p-8 max-w-md w-full">
                      <p className="text-sm text-gray-600 mb-2">総合ランク</p>
                      <span
                        className={`inline-flex items-center justify-center w-20 h-20 rounded-full text-4xl font-bold ${getRankColor(
                          benchmarkData.overall_rank
                        )}`}
                      >
                        {benchmarkData.overall_rank}
                      </span>
                      <p className="mt-4 text-lg font-semibold text-gray-900">
                        スコア: {(benchmarkData.overall_score ?? 0).toFixed(1)}
                      </p>
                      <p className="text-sm text-gray-500">
                        (100が業界平均)
                      </p>
                    </div>
                  </div>

                  {/* 指標別比較 */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">
                      指標別ベンチマーク比較
                    </h3>
                    <div className="space-y-4">
                      {(benchmarkData.comparisons ?? []).map((comp) => (
                        <div
                          key={comp.metric}
                          className="bg-white rounded-lg p-4 border border-gray-200"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">
                              {comp.metric_label}
                            </span>
                            <div className="flex items-center gap-2">
                              {getStatusIcon(comp.status)}
                              <span
                                className={`text-sm font-semibold ${
                                  comp.status === 'above'
                                    ? 'text-green-600'
                                    : comp.status === 'below'
                                    ? 'text-red-600'
                                    : 'text-gray-500'
                                }`}
                              >
                                {(comp.difference_percent ?? 0) >= 0 ? '+' : ''}
                                {(comp.difference_percent ?? 0).toFixed(1)}%
                              </span>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">自社</p>
                              <p className="font-semibold text-gray-900">
                                {(comp.company_value ?? 0).toLocaleString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">業界平均</p>
                              <p className="font-semibold text-gray-700">
                                {(comp.benchmark_avg ?? 0).toLocaleString()}
                              </p>
                            </div>
                            {comp.benchmark_median && (
                              <div>
                                <p className="text-gray-500">中央値</p>
                                <p className="font-semibold text-gray-700">
                                  {comp.benchmark_median.toLocaleString()}
                                </p>
                              </div>
                            )}
                          </div>
                          {/* プログレスバー */}
                          <div className="mt-3">
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  comp.status === 'above'
                                    ? 'bg-green-500'
                                    : comp.status === 'below'
                                    ? 'bg-red-500'
                                    : 'bg-blue-500'
                                }`}
                                style={{
                                  width: `${Math.min(
                                    100,
                                    ((comp.company_value ?? 0) / ((comp.benchmark_avg ?? 1) * 1.5)) * 100
                                  )}%`,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* データなし */}
              {!accountData &&
                !categoryData &&
                !scenarioData &&
                !benchmarkData &&
                !isLoading && (
                  <div className="text-center py-12 text-gray-500">
                    <PieChart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>データがありません</p>
                  </div>
                )}
            </>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
